<template>
  <UIButton
    v-if="canPrintReceipt"
    :label="t('actions.print')"
    :disabled="isPrinting"
    :loading="isPrinting"
    @click="printReceipt(response)"
  />
</template>

<script setup lang="ts">
  import { useProviderConfig } from "@versatile-credit-apply/composition"
  import {
    ApplyResponseCode,
    type ProviderApplyResponseView,
  } from "@versatile-credit-apply/omni-apply-client"
  import {
    useIsReceiptPrinterAvailable,
    useProviderI18n,
    usePrintReceipt,
  } from "@versatile-credit-apply/sdk"
  import { computed, ref, watch } from "vue"

  interface Props {
    response: ProviderApplyResponseView
  }

  const props = defineProps<Props>()

  const config = useProviderConfig(() => props.response.providerKey)
  const isPrinterAvailable = useIsReceiptPrinterAvailable()

  const printMode = computed(() => config.value?.properties?.printMode)

  const isPrinting = ref(false)
  const printReceipt = usePrintReceipt(isPrinting)

  const canPrintReceipt = computed<boolean>(() => {
    if (printMode.value == undefined) {
      return false
    }

    const printableResponseCodes = [
      ApplyResponseCode.APPROVED,
      ApplyResponseCode.DECLINED,
      ApplyResponseCode.PENDING,
    ]

    if (printableResponseCodes.includes(props.response.responseCode)) {
      return isPrinterAvailable.value
    }

    return false
  })

  const { t } = useProviderI18n(() => props.response.providerKey)

  const stop = watch(isPrinterAvailable, async (available) => {
    if (printMode.value !== "automatic") {
      return
    }

    // Print once automatically
    if (available && canPrintReceipt.value) {
      await printReceipt(props.response)
      stop()
    }
  })
</script>

<i18n lang="yaml" locale="en">
actions:
  print: Print Receipt
</i18n>
